import React from 'react';
import { Box, Typography } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useTranslation } from 'react-i18next';
import { InvoiceScriptEnum } from '../../../../common/enums/invoicing.enum';
import { bankAccountDigits, formatBankAccount, isValidBankAccount } from '../../../../utilities/helper/bankAccount.helper';
import { BillingSettingsFormValues, PURPOSE_QR_MAX, renderPurpose } from '../../validation/billingSettings.schema';

/**
 * The fixed words printed on the invoice, in the script the invoice is printed in. They
 * mirror the backend's invoice-labels, not the admin's language: a Cyrillic invoice says
 * ПИБ whatever language the admin panel is in.
 */
const PRINTED = {
  [InvoiceScriptEnum.LATIN]: {
    invoice: 'RAČUN',
    pib: 'PIB',
    mb: 'MB',
    account: 'Tekući račun',
    paymentCode: 'Šifra plaćanja',
    purpose: 'Svrha uplate',
    vat: 'PDV'
  },
  [InvoiceScriptEnum.CYRILLIC]: {
    invoice: 'РАЧУН',
    pib: 'ПИБ',
    mb: 'МБ',
    account: 'Текући рачун',
    paymentCode: 'Шифра плаћања',
    purpose: 'Сврха уплате',
    vat: 'ПДВ'
  }
};

const Line: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Typography sx={{ fontSize: 11, lineHeight: 1.5, color: 'text.primary', wordBreak: 'break-word' }}>
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {label}:
    </Box>{' '}
    {value || '—'}
  </Typography>
);

/**
 * "How it looks on the invoice": a scaled-down invoice header that follows the form as it
 * is typed, so the admin sees what a parent will read before saving anything.
 *
 * The QR square is a picture of where the code goes, not a code: generating a real one
 * here would invite scanning a preview. What it does say is whether a code can be made at
 * all - without a valid account and payment code there is none, which is the whole point
 * of the Payment section.
 */
const InvoiceHeaderPreview: React.FC<{ values: BillingSettingsFormValues }> = ({ values }) => {
  const { t } = useTranslation();
  const labels = PRINTED[values.invoiceScript] ?? PRINTED[InvoiceScriptEnum.LATIN];
  const accountOk = isValidBankAccount(values.bankAccountNumber);
  const codeOk = /^\d{3}$/.test(values.paymentCode.trim());
  const purpose = renderPurpose(values.paymentPurposeTemplate.trim());
  const purposeShown = purpose.length > PURPOSE_QR_MAX ? `${purpose.slice(0, PURPOSE_QR_MAX)}…` : purpose;
  const place = [values.postalCode.trim(), values.city.trim()].filter(Boolean).join(' ');
  const ids = [values.pib.trim() && `${labels.pib} ${values.pib.trim()}`, values.mb.trim() && `${labels.mb} ${values.mb.trim()}`].filter(Boolean).join(' · ');

  return (
    <Box component="section" aria-label={t('billing.settings.preview.title')}>
      <Typography component="h2" sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
        {t('billing.settings.preview.title')}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>{t('billing.settings.preview.description')}</Typography>

      <Box sx={{ border: 1, borderColor: 'line', borderRadius: 4, bgcolor: 'surface.main', p: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word', color: values.legalName.trim() ? 'text.primary' : 'text.disabled' }}>
              {values.legalName.trim() || t('billing.settings.preview.noName')}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.5 }}>
              {[values.address.trim(), place].filter(Boolean).join(', ') || '—'}
            </Typography>
            {ids && <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.5 }}>{ids}</Typography>}
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'text.secondary', flexShrink: 0 }}>{labels.invoice}</Typography>
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'line', my: 1.5 }} />

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          {accountOk && codeOk ? (
            <Box
              aria-label={t('billing.settings.preview.qr')}
              role="img"
              sx={{ width: 64, height: 64, flexShrink: 0, border: 1, borderColor: 'line', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <QrCode2Icon sx={{ fontSize: 52, color: 'text.primary' }} />
            </Box>
          ) : (
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                border: 1,
                borderStyle: 'dashed',
                borderColor: 'warning.main',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: 10,
                color: 'warning.main'
              }}
            >
              {t('billing.settings.preview.noQr')}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Line
              label={labels.account}
              value={accountOk ? formatBankAccount(bankAccountDigits(values.bankAccountNumber)) : values.bankAccountNumber.trim()}
            />
            <Line label={labels.paymentCode} value={values.paymentCode.trim()} />
            <Line label={labels.purpose} value={purposeShown} />
          </Box>
        </Box>
        {!(accountOk && codeOk) && (
          <Typography sx={{ fontSize: 11, color: 'warning.main', mt: 1 }}>
            {!accountOk ? t('billing.settings.preview.noQrAccount') : t('billing.settings.preview.noQrCode')}
          </Typography>
        )}

        <Box sx={{ borderTop: 1, borderColor: 'line', my: 1.5 }} />

        <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.5, wordBreak: 'break-word' }}>
          {values.isInVatSystem
            ? `${labels.vat}: ${values.vatRegistrationNumber.trim() || '—'}`
            : values.vatExemptionNote.trim() || t('billing.settings.preview.noExemption')}
        </Typography>
        {values.invoiceGreetingMessage.trim() && (
          <Typography sx={{ fontSize: 11, mt: 1, lineHeight: 1.5, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{values.invoiceGreetingMessage.trim()}</Typography>
        )}
        {values.invoiceFooterNote.trim() && (
          <Typography sx={{ fontSize: 9, mt: 0.5, color: 'text.secondary', lineHeight: 1.5, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {values.invoiceFooterNote.trim()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InvoiceHeaderPreview;
